import React, { useState } from 'react';
import { Avatar, Box, Button, Chip, IconButton, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { activeBrandDashboardBrandSelector } from '../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import {
    BRAND_LUXE_GOLD,
    BRAND_LUXE_MUTED,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
} from './brandDashboardTokens';
import { BrandOnboardingModal } from './BrandOnboardingModal';

const SOCIAL_LABELS: Record<string, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    facebook: 'Facebook',
    twitter: 'X (Twitter)',
    linkedin: 'LinkedIn',
    thspian: 'Thspian',
};

export default function BrandProfilePage() {
    const brand = useSelector(activeBrandDashboardBrandSelector);
    const nav = useNavigate();
    const [editOpen, setEditOpen] = useState(false);

    if (!brand) {
        return (
            <Box sx={{ py: 10, textAlign: 'center' }}>
                <Typography sx={{ color: BRAND_LUXE_MUTED, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    No brand selected
                </Typography>
            </Box>
        );
    }

    const socials = brand.social_links ? Object.entries(brand.social_links).filter(([, v]) => !!v) : [];

    return (
        <Box sx={{ maxWidth: 720, mx: 'auto', py: { xs: 2, md: 4 } }}>
            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 3,
                    overflow: 'hidden',
                }}>
                <Box
                    sx={{
                        height: { xs: 100, sm: 140 },
                        background: brand.feature_image
                            ? `url(${brand.feature_image}) center/cover no-repeat`
                            : 'linear-gradient(135deg, rgba(255,193,7,0.15) 0%, rgba(0,230,195,0.08) 100%)',
                        position: 'relative',
                    }}>
                    <IconButton
                        size="small"
                        onClick={() => setEditOpen(true)}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}>
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, mt: -5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2 }}>
                        {brand.logo ? (
                            <Box
                                component="img"
                                src={brand.logo}
                                alt={brand.name}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    border: '3px solid #111',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: '3px solid #111',
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: BRAND_LUXE_GOLD,
                                }}>
                                {(brand.name || '?')[0]}
                            </Avatar>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 22, sm: 26 },
                                fontWeight: 900,
                                color: '#fff',
                            }}>
                            {brand.name}
                        </Typography>
                        {brand.verified && <VerifiedIcon sx={{ fontSize: 20, color: BRAND_LUXE_GOLD }} />}
                    </Box>

                    {brand.industry && (
                        <Chip
                            label={brand.industry}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,193,7,0.1)',
                                color: BRAND_LUXE_GOLD,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 24,
                                mb: 1.5,
                            }}
                        />
                    )}

                    {brand.description && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                lineHeight: 1.6,
                                color: 'rgba(255,255,255,0.75)',
                                mb: 2,
                            }}>
                            {brand.description}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        {brand.website && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LanguageOutlinedIcon sx={{ fontSize: 16, color: BRAND_LUXE_MUTED }} />
                                <Typography
                                    component="a"
                                    href={brand.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontSize: 13,
                                        color: BRAND_LUXE_GOLD,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}>
                                    {brand.website.replace(/^https?:\/\//, '')}
                                </Typography>
                            </Box>
                        )}
                        {brand.location?.address && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PlaceOutlinedIcon sx={{ fontSize: 16, color: BRAND_LUXE_MUTED }} />
                                <Typography sx={{ fontSize: 13, color: BRAND_LUXE_MUTED }}>
                                    {brand.location.address}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {socials.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 3,
                        p: { xs: 2.5, sm: 3 },
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 15,
                            color: '#fff',
                            mb: 1.5,
                        }}>
                        Social links
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {socials.map(([key, url]) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        color: BRAND_LUXE_MUTED,
                                        minWidth: 90,
                                    }}>
                                    {SOCIAL_LABELS[key] ?? key}
                                </Typography>
                                <Typography
                                    component="a"
                                    href={url!.startsWith('http') ? url : `https://${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    noWrap
                                    sx={{
                                        fontSize: 13,
                                        color: BRAND_LUXE_GOLD,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}>
                                    {url}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                    onClick={() => setEditOpen(true)}
                    startIcon={<EditOutlinedIcon />}
                    sx={{
                        px: 3,
                        py: 1,
                        borderRadius: 999,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        textTransform: 'none',
                        color: '#392700',
                        background: 'linear-gradient(to right, #ffc965, #feb700)',
                        boxShadow: '0 10px 30px rgba(254,183,0,0.3)',
                        '&:hover': { filter: 'brightness(1.05)', transform: 'scale(1.02)' },
                    }}>
                    Edit profile
                </Button>
            </Box>

            {brand.createdAt && (
                <Typography
                    sx={{
                        mt: 3,
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.3)',
                        textAlign: 'center',
                    }}>
                    Brand created{' '}
                    {new Date(brand.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </Typography>
            )}

            <BrandOnboardingModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                initialData={brand}
                onGoToWallet={() => nav(ROUTES.BRAND_WALLET)}
            />
        </Box>
    );
}
